import { useCallback, useState } from 'react';

import { ConfirmationStatus } from '@website/app/[locale]/activity/[id]/slots/lib/types';

import { StepName, useBookingSteps } from './useBookingSteps';

export type { StepName };

export const useBookingFlow = (isAtHomeActivity: boolean, initialStep?: string) => {
  // The chosen slot's resolved location kind (3-value), synced from the booking
  // form by the flow component. Only an at_home pick needs the address step.
  const [chosenSlotKind, setChosenSlotKind] = useState<'venue' | 'at_home' | 'online' | undefined>(undefined);
  // The booking's location-kind fallback (from the URL/both-mode pick), consulted
  // only when no per-slot location kind was captured. Mirrors the 3rd argument the
  // validators and continue router pass, so the flow stays aligned with them.
  const [chosenLocationKind, setChosenLocationKind] = useState<'venue' | 'at_home' | 'online' | undefined>(undefined);
  const bookingSteps = useBookingSteps(isAtHomeActivity, chosenSlotKind, chosenLocationKind);

  const validateInitialStep = (step: string): StepName => {
    const stepExists = bookingSteps.stepFlow.some((s) => s.key === step);
    if (stepExists) {
      return step as StepName;
    }
    return bookingSteps.stepFlow[0]?.key || 'select-time';
  };

  // State management - now using string-based step names
  const [currentStep, setCurrentStep] = useState<StepName>(
    initialStep ? validateInitialStep(initialStep) : 'select-time'
  );
  const [selectedSlot, setSelectedSlot] = useState<'SINGLE' | 'GROUP' | 'RANGE'>('SINGLE');
  const [bookingStatus, setBookingStatus] = useState<ConfirmationStatus>('pending');
  const [bookingId, setBookingId] = useState<string | undefined>(undefined);

  // Navigation functions
  const nextStep = useCallback(() => {
    const next = bookingSteps.getNextStep(currentStep);
    if (next) {
      setCurrentStep(next);
    }
  }, [currentStep, bookingSteps]);

  const previousStep = useCallback(() => {
    const previous = bookingSteps.getPreviousStep(currentStep);
    if (previous) {
      setCurrentStep(previous);
    }
  }, [currentStep, bookingSteps]);

  const goToStep = useCallback((step: StepName) => {
    setCurrentStep(step);
  }, []);

  // Booking status management
  const updateBookingStatus = useCallback((status: ConfirmationStatus, id?: string) => {
    setBookingStatus(status);
    if (id) {
      setBookingId(id);
    }
  }, []);

  return {
    // State
    currentStep,
    setCurrentStep,
    selectedSlot,
    setSelectedSlot,
    bookingStatus,
    bookingId,

    // Step navigation
    nextStep,
    previousStep,
    goToStep,

    // Booking status management
    updateBookingStatus,

    // Chosen slot's resolved kind (drives whether the address step is needed)
    chosenSlotKind,
    setChosenSlotKind,

    // Booking location-kind fallback (consulted when no per-slot kind is captured)
    chosenLocationKind,
    setChosenLocationKind,

    // Step information
    getStepTitle: useCallback(() => bookingSteps.getStepTitle(currentStep), [bookingSteps, currentStep]),
    getStepNumber: useCallback(() => bookingSteps.getStepNumber(currentStep), [bookingSteps, currentStep]),
    getTotalSteps: useCallback(() => bookingSteps.getTotalSteps(), [bookingSteps]),
    isFirstStep: useCallback(() => bookingSteps.isFirstStep(currentStep), [bookingSteps, currentStep]),
    isLastStep: useCallback(() => bookingSteps.isLastStep(currentStep), [bookingSteps, currentStep])
  };
};
